import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reportsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  TrendingUp, TrendingDown, DollarSign, FileText, Receipt, Store,
  FolderOpen, Loader2, BarChart3
} from 'lucide-react';

const fmt = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reportsAPI.financial()
      .then(res => setData(res.data))
      .catch(err => console.error('Failed to load reports:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div></Layout>;
  }

  const o = data?.overview || {};
  const projectProfitability = data?.project_profitability || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight flex items-center gap-3">
            <BarChart3 className="h-7 w-7 text-muted-foreground" />Financial Reports
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Revenue, profitability, and spending overview</p>
        </div>

        {/* Revenue Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Invoiced</p>
              <p className="text-2xl font-light mt-1">{fmt(o.total_invoiced)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Paid</p>
              <p className="text-2xl font-light mt-1 text-green-600">{fmt(o.total_paid)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Outstanding</p>
              <p className="text-2xl font-light mt-1 text-amber-600">{fmt(o.total_outstanding)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Overdue</p>
              <p className="text-2xl font-light mt-1 text-red-600">{fmt(o.total_overdue)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Profitability */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Vendor Spend</p>
              <p className="text-2xl font-light mt-1">{fmt(o.total_vendor_spend)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Gross Profit</p>
              <p className={`text-2xl font-light mt-1 ${o.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {fmt(o.gross_profit)}
              </p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                {o.gross_profit >= 0 ? <TrendingUp className="h-3 w-3 text-green-600" /> : <TrendingDown className="h-3 w-3 text-red-600" />}
                Paid minus vendor spend
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Proposals</p>
              <p className="text-2xl font-light mt-1">{o.total_proposals}</p>
              <p className="text-xs text-muted-foreground mt-1">Value: {fmt(o.total_proposal_value)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Markup Earned</p>
              <p className="text-2xl font-light mt-1 text-green-600">{fmt(o.total_markup_earned)}</p>
              <p className="text-xs text-muted-foreground mt-1">From approved proposals</p>
            </CardContent>
          </Card>
        </div>

        {/* Project Profitability Table */}
        <Card>
          <CardHeader><CardTitle className="text-base">Project Profitability</CardTitle></CardHeader>
          <CardContent className="p-0">
            {projectProfitability.length === 0 ? (
              <div className="py-12 text-center"><FolderOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No financial data per project yet</p></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Project</TableHead>
                    <TableHead className="text-xs text-right">Invoiced</TableHead>
                    <TableHead className="text-xs text-right">Paid</TableHead>
                    <TableHead className="text-xs text-right">Vendor Spend</TableHead>
                    <TableHead className="text-xs text-right">Budget Actual</TableHead>
                    <TableHead className="text-xs text-right">Profit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projectProfitability.map(p => (
                    <TableRow key={p.project_id} data-testid={`report-project-${p.project_id}`}>
                      <TableCell>
                        <Link to={`/projects/${p.project_id}`} className="font-medium text-sm hover:text-primary transition-colors">
                          {p.project_name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right text-sm">{fmt(p.invoiced)}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(p.paid)}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(p.vendor_spend)}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(p.budget_actual)}</TableCell>
                      <TableCell className={`text-right text-sm font-medium ${p.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmt(p.profit)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
